import {
    useRef,
    isValidElement,
    Children,
    memo,
    useMemo,
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
} from "react";
import { useRecordContext, DatagridBody, useListContext } from "react-admin";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Box, Checkbox, TableCell, TableRow, IconButton } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DatagridWithHistory } from "shared/src/components/DatagridWithHistory";

const DragContext = createContext();

const DragProvider = ({ children }) => {
    const [dragState, setDragState] = useState({
        isDragging: false,
        draggedItemId: null,
        selectedIds: [],
        dragTransform: null,
    });

    return (
        <DragContext.Provider value={{ dragState, setDragState }}>{children}</DragContext.Provider>
    );
};

const MyDatagridRow = memo(
    ({ onToggleItem, children, selected, selectable, rowClick }) => {
        const record = useRecordContext();
        const { selectedIds } = useListContext();
        const { dragState, setDragState } = useContext(DragContext);

        const dragConfig = useMemo(
            () => ({
                id: record.id,
                data: {
                    type: "product",
                    record,
                    isMultiDrag: selectedIds && selectedIds.length > 1,
                    selectedIds: selectedIds && selectedIds.length > 0 ? selectedIds : [record.id],
                },
            }),
            [record, selectedIds]
        );

        const { attributes, listeners, setNodeRef, transform, isDragging } =
            useDraggable(dragConfig);

        useEffect(() => {
            if (isDragging && !dragState.isDragging) {
                setDragState({
                    isDragging: true,
                    draggedItemId: record.id,
                    selectedIds: selectedIds || [record.id],
                    dragTransform: null,
                });
            } else if (
                !isDragging &&
                dragState.isDragging &&
                dragState.draggedItemId === record.id
            ) {
                setDragState({
                    isDragging: false,
                    draggedItemId: null,
                    selectedIds: [],
                    dragTransform: null,
                });
            }
        }, [
            isDragging,
            record.id,
            selectedIds,
            dragState.isDragging,
            dragState.draggedItemId,
            setDragState,
        ]);

        useEffect(() => {
            if (isDragging && transform) {
                setDragState((prev) => ({
                    ...prev,
                    dragTransform: transform,
                }));
            }
        }, [isDragging, transform, setDragState]);

        const isPartOfDragGroup =
            dragState.isDragging &&
            dragState.selectedIds.includes(record.id) &&
            dragState.selectedIds.length > 1;

        const isCurrentlyDragged = dragState.draggedItemId === record.id;

        const getTransform = useCallback(() => {
            if (isCurrentlyDragged) {
                const baseTransform = CSS.Transform.toString(transform);
                if (!baseTransform) return undefined;

                const translateMatch = baseTransform.match(/translate3d\(([^)]+)\)/);
                if (translateMatch) {
                    return `translate3d(${translateMatch[1]})`;
                }
                return baseTransform;
            } else if (isPartOfDragGroup && dragState.dragTransform) {
                const baseTransform = CSS.Transform.toString(dragState.dragTransform);
                if (!baseTransform) return undefined;

                const translateMatch = baseTransform.match(/translate3d\(([^)]+)\)/);
                const offset = dragState.selectedIds.indexOf(record.id) * 2;
                return `${
                    translateMatch ? `translate3d(${translateMatch[1]})` : baseTransform
                } translateY(${offset}px)`;
            }
            return undefined;
        }, [
            isCurrentlyDragged,
            transform,
            isPartOfDragGroup,
            dragState.dragTransform,
            dragState.selectedIds,
            record.id,
        ]);

        const sx = useMemo(
            () => ({
                transform: getTransform(),
                transition: isDragging || isPartOfDragGroup ? "none" : "transform 200ms ease",
                opacity: isDragging || isPartOfDragGroup ? 0.8 : 1,
                backgroundColor: selected ? "rgba(25, 118, 210, 0.08)" : undefined,
                "&:hover": {
                    backgroundColor: selected ? "rgba(25, 118, 210, 0.12)" : "rgba(0, 0, 0, 0.04)",
                },
                ...(isPartOfDragGroup && {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    zIndex: isCurrentlyDragged ? 1000 : 999,
                    scale: 0.98,
                }),
            }),
            [getTransform, isDragging, selected, isPartOfDragGroup, isCurrentlyDragged]
        );

        const handleCheckboxChange = useMemo(
            () => (onToggleItem ? (event) => onToggleItem(record.id, event) : undefined),
            [onToggleItem, record.id]
        );

        const handleRowClick = useMemo(
            () => (event) => {
                if (isDragging) {
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }

                if (event.target.closest('input[type="checkbox"]')) {
                    return;
                }

                if (rowClick && record) {
                    rowClick(record.id);
                }
            },
            [rowClick, record, isDragging]
        );

        const mappedChildren = useMemo(
            () =>
                Children.map(children, (field) =>
                    isValidElement(field) && (field.props.source || field.props.render) ? (
                        <TableCell key={`${record.id}-${field.props.source || field.props.label}`}>
                            {field}
                        </TableCell>
                    ) : null
                ),
            [children, record.id]
        );

        const checkboxCellRef = useRef(null);

        return record ? (
            <TableRow ref={setNodeRef} sx={sx} onClick={handleRowClick}>
                <TableCell padding="none" ref={checkboxCellRef}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                        {selectable && (
                            <Checkbox checked={selected} onChange={handleCheckboxChange} />
                        )}
                        <IconButton
                            size="small"
                            sx={{ cursor: "grab", "&:active": { cursor: "grabbing" } }}
                            {...attributes}
                            {...listeners}
                        >
                            <DragIndicatorIcon />
                        </IconButton>
                    </Box>
                </TableCell>
                {mappedChildren}
            </TableRow>
        ) : null;
    },
    (prevProps, nextProps) => {
        return (
            prevProps.selected === nextProps.selected &&
            prevProps.selectedIds?.length === nextProps.selectedIds?.length &&
            prevProps.record?.id === nextProps.record?.id &&
            prevProps.children === nextProps.children
        );
    }
);

const CategoryDatagridBody = memo(
    (props) => {
        return <DatagridBody row={<MyDatagridRow />} {...props} />;
    },
    (prevProps, nextProps) => {
        return (
            prevProps.data === nextProps.data &&
            prevProps.selectedIds === nextProps.selectedIds &&
            prevProps.data?.length === nextProps.data?.length &&
            JSON.stringify(prevProps.sort) === JSON.stringify(nextProps.sort)
        );
    }
);

export const CategoryDatagrid = memo(
    (props) => {
        return (
            <DragProvider>
                <DatagridWithHistory body={<CategoryDatagridBody />} {...props} />
            </DragProvider>
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.listItems === nextProps.listItems &&
            prevProps.bulkActionButtons === nextProps.bulkActionButtons &&
            prevProps.children === nextProps.children
        );
    }
);
